import React, { useEffect, useState, useMemo, useCallback, useRef, use } from "react";
import api from "../lib/api";
import "./NoticeCreate.css";
import { useNavigate, useLocation } from "react-router-dom";
import DatePartsInput from "./DatePartsInput";
import { openAuthFile, authFileErrorMessage } from "../lib/authFile";

const NOTICES_PER_PAGE = 10;
const TITLE_HISTORY_KEY = "notice_title_history";
const FORM_DRAFT_KEY = "notice_form_draft_v1";
const MAX_TITLE_HISTORY = 20;

/** 今日の日付を "YYYY-MM-DD" で返す。確認締切日・掲示最終日の下限に使う。 */
const todayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const NoticeCreate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // イベント一覧の「イベント中止」から来た場合に、対象イベントIDを保持する。
  // このお知らせを作成できた時点でイベントを中止済みにする。
  const [cancelEventId, setCancelEventId] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [empLoading, setEmpLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [readDeadlineDate, setReadDeadlineDate] = useState("");
  const [displayDate, setDisplayDate] = useState("");
  const [reviewStep, setReviewStep] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recentNotices, setRecentNotices] = useState([]);
  const [editingNoticeId, setEditingNoticeId] = useState(null);
  const [noticePage, setNoticePage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [titleHistory, setTitleHistory] = useState([]);
  const [activeTab, setActiveTab] = useState("create"); // "create" | "list"
  const [isEditingMode, setIsEditingMode] = useState(false);

  const [allowReply, setAllowReply] = useState(false);
  const [replyTargetMode, setReplyTargetMode] = useState("all");
  const [specificReplyIds, setSpecificReplyIds] = useState([]);
  
  // ファイル添付
  const [pendingFiles, setPendingFiles] = useState([]); // {file, name, size, type}
  const [savedAttachments, setSavedAttachments] = useState([]); // 保存済み添付ファイル
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const [selectedDeptId, setSelectedDeptId] = useState("");
  const [selectedKaName, setSelectedKaName] = useState("");
  const [committeeOnly, setCommitteeOnly] = useState(false);

  const [tempSelectedDepts, setTempSelectedDepts] = useState([]);
  const [tempSelectedSecs, setTempSelectedSecs] = useState([]);
  const [isAllMode, setIsAllMode] = useState(false);

  const [toast, setToast] = useState({ visible: false, text: "", isError: false });

  const showToast = (text, isError = false) => {
    setToast({ visible: true, text, isError });
    setTimeout(() => setToast({ visible: false, text: "", isError: false }), 3500);
  };

  useEffect(() => {
    try {
      const raw = localStorage.getItem(TITLE_HISTORY_KEY);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) setTitleHistory(arr);
      }
    } catch (_) {
      /* ignore */
    }
    try {
      const draftRaw = localStorage.getItem(FORM_DRAFT_KEY);
      if (draftRaw) {
        const d = JSON.parse(draftRaw);
        const hasDraft =
          d &&
          typeof d === "object" &&
          ((d.title && String(d.title).trim()) ||
            (d.content && String(d.content).trim()) ||
            (Array.isArray(d.selectedIds) && d.selectedIds.length > 0));
        if (hasDraft) {
          if (d.title != null) setTitle(String(d.title));
          if (d.content != null) setContent(String(d.content));
          if (Array.isArray(d.selectedIds)) setSelectedIds(d.selectedIds);
          if (d.readDeadlineDate != null) setReadDeadlineDate(String(d.readDeadlineDate));
          if (d.displayDate != null) setDisplayDate(String(d.displayDate));
          if (d.editingNoticeId != null) setEditingNoticeId(d.editingNoticeId);
          if (typeof d.committeeOnly === "boolean") setCommitteeOnly(d.committeeOnly);
          if (d.selectedDeptId != null) setSelectedDeptId(String(d.selectedDeptId));
          if (d.selectedKaName != null) setSelectedKaName(String(d.selectedKaName));
          showToast("一時保存した内容を読み込みました。");
        }
      }
    } catch (_) {
      /* ignore */
    }
  }, []);

  // イベント一覧の「イベント中止」から遷移してきたときの初期値。
  // 上の下書き復元より後に実行して、こちらの内容を優先させる。
  useEffect(() => {
    const st = location.state && location.state.eventCancel;
    if (!st) return;
    if (st.title) setTitle(String(st.title));
    if (st.content) setContent(String(st.content));
    // recipients は memberId（社員番号）の文字列。selectedIds も同じ形式で保持する。
    const memberIds = Array.isArray(st.recipientMemberIds) ? st.recipientMemberIds : [];
    if (memberIds.length > 0) setSelectedIds(memberIds);
    setCancelEventId(st.eventId || null);
    setActiveTab("create");
    // 戻る操作やリロードで再適用されないよう、履歴から state を落とす
    navigate(location.pathname, { replace: true, state: null });
    showToast(
      memberIds.length > 0
        ? `中止のお知らせを作成します。宛先にイベント対象者 ${memberIds.length} 名を選択しました。`
        : "中止のお知らせを作成します。宛先を選択してください。"
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persistTitleHistory = (titles) => {
    try {
      localStorage.setItem(TITLE_HISTORY_KEY, JSON.stringify(titles));
    } catch (_) {
      /* ignore */
    }
  };

  const addTitleToHistory = (t) => {
    const s = (t || "").trim();
    if (!s) return;
    setTitleHistory((prev) => {
      const next = [s, ...prev.filter((x) => x !== s)].slice(0, MAX_TITLE_HISTORY);
      persistTitleHistory(next);
      return next;
    });
  };

  const fetchRecentNotices = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/notices/?company_only=1&_t=${new Date().getTime()}`);
      const raw = res.data.results || res.data;
      const list = Array.isArray(raw) ? raw : [];
      setRecentNotices(
        list.filter((n) => n && n.title !== "スケジュール登録のお願い")
      );
    } catch (err) {
      console.error("最新のお知らせ取得エラー:", err);
    } finally {
      setLoading(false);
    }
  };

  // 保存済み添付ファイルを取得
  const fetchAttachments = useCallback(async (noticeId) => {
    if (!noticeId) { setSavedAttachments([]); return; }
    try {
      const res = await api.get(`/notices/${noticeId}/attachments/`);
      setSavedAttachments(Array.isArray(res.data) ? res.data : []);
    } catch (_) {
      setSavedAttachments([]);
    }
  }, []);

  // ファイルドラッグ&ドロップ
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    const MAX_MB = 20;
    files.forEach((f) => {
      if (f.size > MAX_MB * 1024 * 1024) {
        showToast(`${f.name} はサイズが大きすぎます（上限 ${MAX_MB}MB）。`, true);
        return;
      }
      setPendingFiles((prev) => [...prev, { file: f, name: f.name, size: f.size, type: f.type }]);
    });
  }, []);

  const handleFileSelect = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((f) => {
      setPendingFiles((prev) => [...prev, { file: f, name: f.name, size: f.size, type: f.type }]);
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const removePendingFile = (idx) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const removeAttachment = async (attId) => {
    if (!editingNoticeId) return;
    try {
      await api.delete(`/notices/${editingNoticeId}/attachments/${attId}/`);
      setSavedAttachments((prev) => prev.filter((a) => a.id !== attId));
      showToast("添付ファイルを削除しました。");
    } catch (_) {
      showToast("添付ファイルの削除に失敗しました。", true);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  };

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const res = await api.get("/departments/");
        const list = Array.isArray(res.data) ? res.data : res.data?.results ?? [];
        setDepartments(list);
      } catch (err) {
        console.error("部署一覧の取得エラー:", err);
        setDepartments([]);
      }
    };
    fetchDepartments();
    fetchRecentNotices();
  }, []);

  const selectedDept = departments.find((d) => d.departmentId === selectedDeptId);

  {/*}
  const fetchEmployeesForFilter = useCallback(async () => {
    const today = new Date().toISOString().slice(0, 10);
    if (committeeOnly) {
      setEmpLoading(true);
      try {
        const res = await api.get(`/admin/employees/by_department_name/ALL/`, {
          params: { date: today, committee: 1 },
        });
        const list = Array.isArray(res.data) ? res.data : [];
        setEmployees(
          list.map((r) => ({
            memberId: r.memberId,
            name: r.employeeName || r.name || "",
            departmentName: r.departmentName || "",
            departmentBuName: r.departmentBuName,
            departmentKaName: r.departmentKaName,
          }))
        );
      } catch (err) {
        console.error("委員会・員会の社員取得エラー:", err);
        setEmployees([]);
      } finally {
        setEmpLoading(false);
      }
      return;
    }

    if (!selectedDeptId) {
      setEmployees([]);
      return;
    }

    if (selectedDeptId === ALL_DEPTS_VALUE) {
      setEmpLoading(true);
      try {
        const res = await api.get(`/admin/employees/by_department_name/ALL/`, {
          params: { date: today },
        });
        const list = Array.isArray(res.data) ? res.data : [];
        setEmployees(
          list.map((r) => ({
            memberId: r.memberId,
            name: r.employeeName || r.name || "",
            departmentName: r.departmentName || "",
            departmentBuName: r.departmentBuName,
            departmentKaName: r.departmentKaName,
          }))
        );
      } catch (err) {
        console.error("全社員の取得エラー:", err);
        setEmployees([]);
      } finally {
        setEmpLoading(false);
      }
      return;
    }
    
    const dept = departments.find((d) => d.departmentId === selectedDeptId);
    const nameForApi = selectedKaName || dept?.departmentName || "";
    if (!nameForApi) {
      setEmployees([]);
      return;
    }
    setEmpLoading(true);
    try {
      // 課未選択: 部配下の全課の社員を取得 / 課選択: その課のみ（完全一致）
      const expandSubtree = selectedKaName ? 0 : 1;
      const res = await api.get(
        `/admin/employees/by_department_name/${encodeURIComponent(nameForApi)}/`,
        { params: { date: today, expand: expandSubtree } }
      );
      const list = Array.isArray(res.data) ? res.data : [];
      setEmployees(
        list.map((r) => ({
          memberId: r.memberId,
          name: r.employeeName || r.name || "",
          departmentName: r.departmentName || "",
          departmentBuName: r.departmentBuName,
          departmentKaName: r.departmentKaName,
        }))
      );
    } catch (err) {
      console.error("部署別社員情報の取得エラー:", err);
      setEmployees([]);
    } finally {
      setEmpLoading(false);
    }
  }, [committeeOnly, selectedDeptId, selectedKaName, departments]);
  */}
  {/*}
  useEffect(() => {
    fetchEmployeesForFilter();
  }, [fetchEmployeesForFilter]);
  */}

  const handleAddCheckedQuickly = async () => {
    setEmpLoading(true);
    try {
      let allFetchedEmployees = [];

      // 「部署単位」: チェックした部・課に紐づく社員をAPIから取得
      if (selectedDeptId === "DEPT_SELECT") {
        // 課までチェックした部は、その課だけを宛先にする（部名で引くと expand=1 で
        // 配下の課がすべて展開され、選ばなかった課まで宛先に入ってしまうため）。
        // 課をチェックしていない部は、これまでどおり部全体が宛先。
        const selectedNames = [];
        tempSelectedDepts.forEach(id => {
          const d = departments.find(dept => dept.departmentId === id);
          if (!d) return;
          const checkedSecs = (d.children || []).filter(c => tempSelectedSecs.includes(c.departmentId));
          if (checkedSecs.length > 0) {
            checkedSecs.forEach(sec => selectedNames.push(sec.departmentName));
          } else {
            selectedNames.push(d.departmentName);
          }
        });

        // 重複を排除してから、各部署・課に紐づく社員をAPIから取得
        for (const name of [...new Set(selectedNames)]) {
          const res = await api.get(`/admin/employees/by_department_name/${encodeURIComponent(name)}/`, {
            params: { date: new Date().toISOString().slice(0, 10), expand: 1 }
          });
          allFetchedEmployees = [...allFetchedEmployees, ...res.data];
        }
      } 
      // 「全ての部署」が選択されている場合、在籍の全社員をAPIから取得
      else if (selectedDeptId === "ALL_EMP") {
        const res = await api.get(`/admin/employees/by_department_name/ALL/`);
        allFetchedEmployees = res.data;
      }

      // APIから取得した社員情報を、テーブル表示用の形式に整形。
      // 複数の部署・課を指定すると同じ社員が重複して返るため、社員コードで一意化する
      // （重複したままだと配信人数の表示が水増しされ、テーブルの key も重複する）。
      const seenMemberIds = new Set();
      const formatted = [];
      allFetchedEmployees.forEach(r => {
        if (!r || r.memberId == null) return;
        const memberId = r.memberId;
        if (seenMemberIds.has(memberId)) return;
        seenMemberIds.add(memberId);
        formatted.push({
          memberId,
          name: r.employeeName || r.name || "",
          departmentName: r.departmentName || "",
          departmentBuName: r.departmentBuName,
          departmentKaName: r.departmentKaName,
        });
      });

      setEmployees(formatted);
      // 選択された社員のIDを設定
      setSelectedIds(formatted.map(e => e.memberId));
      
    } catch (err) {
      showToast("社員情報の取得に失敗しました", true);
    } finally {
      setEmpLoading(false);
    }
  };

  useEffect(() => {
    if (selectedDeptId === "ALL_EMP") {
      handleAddCheckedQuickly();
    }
  }, [selectedDeptId]);

  const filteredEmployees = useMemo(() => {
    return employees.filter((emp) => {
      if (!committeeOnly && searchTerm.trim() !== "") {
        const lower = searchTerm.toLowerCase();
        const nameMatch = (emp.name || "").toLowerCase().includes(lower);
        const idMatch = String(emp.memberId).includes(lower);
        if (!(nameMatch || idMatch)) return false;
      }
      return true;
    });
  }, [employees, searchTerm, committeeOnly]);

  /** 部署ツリーから所属文字列を部（…00）と課に分離（API 未返却時の補助） */
  const getBuKaFromDeptTree = useCallback(
    (empDnRaw) => {
      const dn = String(empDnRaw || "")
        .trim()
        .replace(/\s+/g, " ");
      if (!dn) return { buName: "—", kaName: "—" };
      const bus = (departments || []).filter(
        (d) =>
          d.departmentId &&
          d.departmentId.endsWith("00") &&
          !d.departmentId.startsWith("00")
      );
      for (const buDept of bus) {
        const buName = String(buDept.departmentName || "")
          .trim()
          .replace(/\s+/g, " ");
        if (dn === buName) {
          return { buName: buName, kaName: "—" };
        }
        for (const c of buDept.children || []) {
          const kn = String(c.departmentName || "")
            .trim()
            .replace(/\s+/g, " ");
          if (kn && dn === kn) {
            return { buName: buName, kaName: kn };
          }
        }
      }
      return { buName: dn, kaName: "—" };
    },
    [departments]
  );

  /** テーブル表示用：部署名（部）と課を分離（API の departmentBuName / departmentKaName を優先） */
  const getBuKaForEmployee = useCallback(
    (emp) => {
      const buApi = (emp.departmentBuName != null ? String(emp.departmentBuName) : "").trim();
      const kaApi = (emp.departmentKaName != null ? String(emp.departmentKaName) : "").trim();
      if (buApi || kaApi) {
        if (!buApi && kaApi) {
          const t = getBuKaFromDeptTree((emp.departmentName || "").trim());
          return {
            buName: t.buName !== "—" ? t.buName : "—",
            kaName: kaApi,
          };
        }
        return {
          buName: buApi || "—",
          kaName: kaApi || "—",
        };
      }
      const empDn = (emp.departmentName || "").trim();
      if (committeeOnly) {
        return { buName: empDn || "—", kaName: "—" };
      }
      if (!selectedDeptId) {
        return getBuKaFromDeptTree(empDn);
      }
      if (selectedDeptId === "ALL_EMP" || selectedDeptId === "DEPT_SELECT") {
        return getBuKaFromDeptTree(empDn);
      }
      const bu = (selectedDept?.departmentName || "").trim();
      if (selectedKaName) {
        return { buName: bu || "—", kaName: selectedKaName };
      }
      if (empDn === bu) {
        return { buName: bu, kaName: "—" };
      }
      const childNames = new Set(
        (selectedDept?.children || [])
          .map((c) => (c.departmentName || "").trim())
          .filter(Boolean)
      );
      if (childNames.has(empDn)) {
        return { buName: bu, kaName: empDn };
      }
      return {
        buName: bu || empDn || "—",
        kaName: empDn && empDn !== bu ? empDn : "—",
      };
    },
    [
      committeeOnly,
      selectedDept,
      selectedKaName,
      selectedDeptId,
      getBuKaFromDeptTree,
    ]
  );

  const handleSelectAllFiltered = () => {
    const currentFilteredIds = filteredEmployees.map((e) => e.memberId);
    const allFilteredSelected = currentFilteredIds.every((id) => selectedIds.includes(id));
    if (allFilteredSelected) {
      setSelectedIds((prev) => prev.filter((id) => !currentFilteredIds.includes(id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...currentFilteredIds])]);
    }
  };

  useEffect(() => {
    const currentFilteredIds = filteredEmployees.map((e) => e.memberId);
    if (currentFilteredIds.length === 0) {
      setSelectAll(false);
      return;
    }
    const allSelected = currentFilteredIds.every((id) => selectedIds.includes(id));
    setSelectAll(allSelected);
  }, [selectedIds, filteredEmployees]);

  const handleSelectOne = (memberId) => {
    setSelectedIds((prev) =>
      prev.includes(memberId) ? prev.filter((id) => id !== memberId) : [...prev, memberId]
    );
  };

  const handleEditClick = async (notice) => {
    setLoading(true);
    setReviewStep(false);
    setIsEditingMode(false);
    showToast(`「${notice.title}」の情報を読み込み中...`);
    try {
      const res = await api.get(`/notices/${notice.id}/`);
      const fullNotice = res.data;
      setEditingNoticeId(fullNotice.id);
      setTitle(fullNotice.title);
      setContent(fullNotice.message);
      const rd = fullNotice.read_deadline_date;
      setReadDeadlineDate(rd ? String(rd).slice(0, 10) : "");
      const pe = fullNotice.publish_end_at;
      setDisplayDate(pe ? String(pe).slice(0, 10) : "");
      // 返信設定も読み込む（読み込まないと再保存で返信なしに戻ってしまう）
      setAllowReply(Boolean(fullNotice.allow_reply));
      setReplyTargetMode(fullNotice.reply_target_mode || "all");
      setSpecificReplyIds((fullNotice.reply_required_recipients || []).map(String));
      const recipientsArray = fullNotice.recipients || [];
      const initialSelectedMemberIds = recipientsArray
        .map((x) => {
          if (x == null) return null;
          if (typeof x === "object" && x.memberId != null) return String(x.memberId);
          return String(x);
        })
        .filter(Boolean);
      setSelectedIds(initialSelectedMemberIds);
      //送った社員を表示
      try {
        setEmpLoading(true);
        const empRes = await api.get(`/admin/employees/by_department_name/ALL/`);
        const allEmps = Array.isArray(empRes.data) ? empRes.data : [];
        
        const formatted = allEmps.map((r) => ({
          memberId: r.memberId,
          name: r.employeeName || r.name || "",
          departmentName: r.departmentName || "",
          departmentBuName: r.departmentBuName,
          departmentKaName: r.departmentKaName,
        }));
        const onlyRecipients = formatted.filter((emp) =>
        initialSelectedMemberIds.includes(emp.memberId)
        );
        setEmployees(onlyRecipients);
        setSelectedDeptId("");
      } catch(empErr) {
        console.error("エラーが発生しました", empErr);
      }finally {
        setEmpLoading(false);
      }


      setSelectAll(false);
      setPendingFiles([]);
      await fetchAttachments(fullNotice.id);
      setActiveTab("create");
    } catch (err) {
      console.error("お知らせ詳細情報の取得エラー:", err);
      showToast("お知らせの詳細情報の読み込みに失敗しました。", true);
    } finally {
      setLoading(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // 修正ボタン
  const handleStartEdit = () => {
    setIsEditingMode(true);
    showToast("編集可能になりました。");
  };

  const resetForm = () => {
    setTitle("");
    setContent("");
    setReadDeadlineDate("");
    setDisplayDate("");
    setSelectedIds([]);
    setSelectAll(false);
    setEditingNoticeId(null);
    setReviewStep(false);
    setPendingFiles([]);
    setAllowReply(false);
    setReplyTargetMode("all");
    setSpecificReplyIds([]);
    setSavedAttachments([]);
    setCancelEventId(null);
  };

  const handleNewNotice = () => {
    resetForm();
    showToast("新規作成モードに切り替えました。");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // 複製：現在の内容（件名・本文・日付・配信先）を残したまま「新規のお知らせ」にする。
  // 同じような内容を少しだけ修正して別の人に送りたいときに、作り直さずに済む。
  {/*const handleDuplicate = () => {
    setEditingNoticeId(null);   // 保存時に新規お知らせとして作成される
    setReviewStep(false);
    setSavedAttachments([]);    // 添付は元のお知らせのものなので引き継がない
    setPendingFiles([]);
    showToast("内容を複製しました。修正して新しいお知らせとして配信できます。");
    window.scrollTo({ top: 0, behavior: "smooth" });
    // 件名・本文・確認締切日・掲示最終日・配信先(selectedIds) はそのまま維持
  };
  */}
  
  const handleDuplicate = () => {
    setEditingNoticeId(null);   // 新規作成扱いにする
    setTitle("");               // ★ご要望①: タイトル（タイトル）を空にする
    setIsEditingMode(true);    // 編集モードにする
    setReviewStep(false);
    setAllowReply(false);
    setSavedAttachments([]);    // 添付ファイルは引き継がない
    setPendingFiles([]);
    showToast("内容を複製しました。タイトルを入力し、修正して新しいお知らせとして配信できます。");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const handleConfirmClick = () => {
    if (!title.trim() || !content.trim()) {
      showToast("タイトルと内容を入力してください。", true);
      return;
    }
    if (selectedIds.length === 0) {
      showToast("配信先の社員を1名以上選択してください。", true);
      return;
    }
    setReviewStep(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleReviewBack = () => {
    setReviewStep(false);
  };

  const handleFinalSave = async () => {
    // 新規作成では過去日を認めない（手入力された場合の最終防止）。
    // 既存お知らせの修正時は、当時の締切日をそのまま残せるよう制限しない。
    if (!editingNoticeId) {
      const today = todayYmd();
      if (readDeadlineDate && readDeadlineDate < today) {
        showToast("確認締切日に過去の日付は指定できません。", true);
        return;
      }
      if (displayDate && displayDate < today) {
        showToast("掲示最終日に過去の日付は指定できません。", true);
        return;
      }
    }
    // 誤配信防止: 配信人数を明示して最終確認
    const _who = editingNoticeId ? "更新して再配信" : "配信";
    if (!window.confirm(`このお知らせを ${selectedIds.length} 名に${_who}します。よろしいですか？`)) {
      return;
    }
    setLoading(true);
    try {
      const payload = {
        title: title.trim(),
        message: content.trim(),
        active: true,
        recipients: selectedIds,
        read_deadline_date: readDeadlineDate || null,
        display_date: displayDate || null,
        allow_reply: allowReply,
        reply_target_mode: replyTargetMode,
        reply_required_recipients: replyTargetMode === "specific" ? specificReplyIds : [],
      };
      let noticeId = editingNoticeId;
      const isNew = !editingNoticeId;

      if (editingNoticeId) {
        await api.patch(`/notices/${editingNoticeId}/`, payload);
      } else {
        // イベント中止連絡はイベントに紐づける（イベント権限だけの担当者も作成できる）
        const res = await api.post(
          "/notices/",
          cancelEventId ? { ...payload, cancel_event_id: cancelEventId } : payload
        );
        noticeId = res.data?.id;
      }
      // イベント中止のお知らせだった場合、配信できたこの時点でイベントを中止済みにする。
      // skip_notification を付けるのは、サーバ側の自動生成お知らせと二重配信になるため。
      if (isNew && cancelEventId) {
        try {
          await api.post(`/events/${cancelEventId}/cancel/`, {
            cancel_reason: content.trim(),
            skip_notification: true,
          });
          setCancelEventId(null);
        } catch (err) {
          showToast(
            err.response?.data?.error ||
              "お知らせは配信しましたが、イベントの中止登録に失敗しました。イベント一覧から再度お試しください。",
            true
          );
        }
      }
      // 添付ファイルのアップロード
      if (noticeId && pendingFiles.length > 0) {
        for (const pf of pendingFiles) {
          const fd = new FormData();
          fd.append("file", pf.file);
          try {
            await api.post(`/notices/${noticeId}/attachments/`, fd, {
              headers: { "Content-Type": "multipart/form-data" },
            });
          } catch (_) {
            showToast(`${pf.name} のアップロードに失敗しました。`, true);
          }
        }
        setPendingFiles([]);
      }
      addTitleToHistory(title.trim());
      await fetchRecentNotices();
      // 新規作成・修正のどちらも、保存したら完了とみなして新規作成画面に戻す。
      // （新規作成後に編集モードへ移行すると、一覧にそのお知らせが「編集中」と出てしまうため）
      resetForm();
      showToast(
        isNew
          ? "お知らせを作成しました。新規作成画面に戻りました。"
          : "お知らせを更新しました。新規作成画面に戻りました。"
      );
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error("お知らせの保存/更新エラー:", err);
      showToast("お知らせの保存/更新に失敗しました。", true);
    } finally {
      setLoading(false);
    }
  };

  const handleTempSave = () => {
    try {
      localStorage.setItem(
        FORM_DRAFT_KEY,
        JSON.stringify({
          title,
          content,
          selectedIds,
          readDeadlineDate,
          displayDate,
          editingNoticeId,
          committeeOnly,
          selectedDeptId,
          selectedKaName,
        })
      );
      showToast("一時保存しました（この端末に保存）。");
    } catch (_) {
      showToast("一時保存に失敗しました。", true);
    }
  };

  const handleCancel = () => {
    const wasEditing = !!editingNoticeId;
    if (
      !title.trim() &&
      !content.trim() &&
      selectedIds.length === 0 &&
      !readDeadlineDate && !displayDate
    ) {
      setReviewStep(false);
      if (wasEditing) {
        resetForm();
        setActiveTab("list");
        fetchRecentNotices();
      }
      return;
    }
    if (!window.confirm("入力内容を破棄してよろしいですか？")) return;
    resetForm();
    showToast("キャンセルしました。");
    // 修正のキャンセル時は「お知らせ一覧」へ戻る
    if (wasEditing) {
      setActiveTab("list");
      fetchRecentNotices();
    }
  };

  // ① お知らせ自体の削除（一覧から）
  const handleDeleteNotice = async (notice, e) => {
    if (e) e.stopPropagation(); // 行クリック（編集遷移）を抑止
    if (!window.confirm(`お知らせ「${notice.title}」を削除しますか？\nこの操作は取り消せません。`)) return;
    try {
      // 管理者権限が必要な専用エンドポイントを使用（一般社員は403で拒否される）
      await api.delete(`/admin/notifications/${notice.id}/delete/`);
      showToast("お知らせを削除しました。");
      if (editingNoticeId === notice.id) resetForm();
      fetchRecentNotices();
    } catch (err) {
      console.error("お知らせの削除エラー:", err);
      showToast("お知らせの削除に失敗しました。", true);
    }
  };

  const handleBack = () => {
    navigate("/dashboard");
  };

  const openReadersPage = (notice, e) => {
    e.stopPropagation();
    const base = window.location.pathname.startsWith("/employee") ? "/employee" : "";
    const url = `${window.location.origin}${base}/admin/notice/${notice.id}/readers`;
    window.open(url, "_blank");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}/${month}/${day}`;
  };

  const selectedPreviewNames = useMemo(() => {
    const set = new Set(selectedIds);
    return employees
      .filter((e) => set.has(e.memberId))
      .map((e) => `${e.name}（${e.memberId}）`);
  }, [employees, selectedIds]);

  return (
    <div className="notice-create-container">
      <div className="notice-create-header">
        <h2 className="notice-create-title">お知らせ</h2>
        <button className="notice-create-back-btn" onClick={handleBack} disabled={loading}>
          メニュー画面に戻る
        </button>
      </div>

      {/* タブ */}
      <div className="notice-tabs no-print">
        <button
          className={`notice-tab-btn${activeTab === "create" ? " notice-tab-active" : ""}`}
          onClick={() => setActiveTab("create")}
          type="button"
        >
          ✏️ お知らせ作成
        </button>
        <button
          className={`notice-tab-btn${activeTab === "list" ? " notice-tab-active" : ""}`}
          onClick={() => { setActiveTab("list"); fetchRecentNotices(); }}
          type="button"
        >
          📋 お知らせ一覧
        </button>
      </div>

      {activeTab === "create" ? (
        <div className="content-wrapper">
          <div className="employee-panel">
            <div className="filter-controls">
              <div className="filter-label-row">
                <span className="filter-label-block">配信先の絞り込み</span>
              </div>
              <div className="filter-controls-inline">
                <label className="committee-check filter-inline-item">
                  <input
                    type="checkbox"
                    checked={selectedDeptId === "ALL_EMP"}
                    onChange={() => {
                      setSelectedDeptId((prev) => prev === "ALL_EMP" ? "" : "ALL_EMP");
                      setCommitteeOnly(false);
                      if (selectedDeptId === "ALL_EMP") {
                        setEmployees([]);
                        setSelectedIds([]);
                      }
                    }}
                  />
                  全社員
                </label>
                <label className="filter-inline-item">
                  <input
                    type="checkbox"
                    checked={selectedDeptId === "DEPT_SELECT"}
                    onChange={() => {
                      setSelectedDeptId((prev) => prev === "DEPT_SELECT" ? "" : "DEPT_SELECT");
                      setCommitteeOnly(false);
                      if (selectedDeptId === "DEPT_SELECT") {
                        setTempSelectedDepts([]);
                        setTempSelectedSecs([]);
                        setEmployees([]);
                        setSelectedIds([]);
                      }
                    }}
                  />
                  部署単位
                </label>
              </div>
              {selectedDeptId === "DEPT_SELECT" && (
                <div className="dept-inline-selector" style={{ border: '1px solid #ddd', padding: '10px', backgroundColor: '#fafafa', marginTop: '10px' }}>
                  <div style={{ fontSize: '0.85em', color: '#555', marginBottom: '8px' }}>部署を選択すると右側に課が表示されます</div>
                  {departments.filter(d => d.departmentId && d.departmentId.endsWith("00") && !d.departmentId.startsWith("00")).map((dept) => {
                    const isChecked = tempSelectedDepts.includes(dept.departmentId);
                    const sections = dept.children || [];
                    return (
                      <div key={dept.departmentId} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', padding: '4px 0', borderBottom: '1px dashed #eee', gap: '6px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', minWidth: '120px' }}>
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => setTempSelectedDepts(prev => prev.includes(dept.departmentId) ? prev.filter(id => id !== dept.departmentId) : [...prev, dept.departmentId])}
                            style={{ marginRight: '6px' }}
                          />
                          <span style={{ fontSize: '0.95em' }}>{dept.departmentName}</span>
                        </label>
                        {isChecked && sections.map(sec => (
                          <label key={sec.departmentId} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', backgroundColor: '#e8f4fd', borderRadius: '4px', padding: '2px 8px', fontSize: '0.88em' }}>
                            <input
                              type="checkbox"
                              checked={tempSelectedSecs.includes(sec.departmentId)}
                              onChange={() => setTempSelectedSecs(prev => prev.includes(sec.departmentId) ? prev.filter(id => id !== sec.departmentId) : [...prev, sec.departmentId])}
                              style={{ marginRight: '4px' }}
                            />
                            <span>{sec.departmentName}</span>
                          </label>
                        ))}
                      </div>
                    );
                  })}
                  <div style={{ marginTop: '8px' }}>
                    <button type="button" className="btn-add-tag" onClick={handleAddCheckedQuickly}>
                      チェックした部署・課を表示
                    </button>
                  </div>
                </div>
              )}
            </div>
            <p className="filter-hint">
              {committeeOnly
                ? "該当者は下のテーブルに表示されます。名前・社員コードの入力欄はありません。"
                : selectedDeptId === "ALL_EMP"
                  ? "「全社員」を選ぶと、在籍の全社員が一覧に表示されます。"
                  : "部だけを選ぶと、その部に紐づく全ての課の社員が表示されます。課まで選ぶとその課のみです。"}
            </p>
            <div className="employee-table-section">
              {empLoading && (
                <div style={{ padding: 10, color: "#666" }}>社員一覧を読み込み中...</div>
              )}
              {!empLoading && !committeeOnly && !selectedDeptId && (
                <div style={{ padding: 10, color: "#6b7280" }}>上の「全社員」または「部署単位」を選んでください。</div>
              )}
              <table className="employee-table">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAllFiltered}
                      />
                    </th>
                    <th>社員コード</th>
                    <th>社員名</th>
                    <th>部署</th>
                    <th>課</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEmployees.map((emp) => {
                    const { buName, kaName } = getBuKaForEmployee(emp);
                    return (
                      <tr key={emp.memberId}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(emp.memberId)}
                            onChange={() => handleSelectOne(emp.memberId)}
                          />
                        </td>
                        <td>{emp.memberId}</td>
                        <td>{emp.name}</td>
                        <td>{buName}</td>
                        <td>{kaName}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {selectedIds.length > 0 && (
              <div className="selected-count">{selectedIds.length}名選択中</div>
            )}
          </div>

          <div className="notice-form-section">
            {reviewStep ? (
              <div className="notice-review-panel">
                <h3 className="notice-review-heading">入力内容の確認</h3>
                <div className="notice-review-recipient-banner">
                  この内容を <strong>{selectedIds.length}名</strong> に配信します
                </div>
                <dl className="notice-review-dl">
                  <dt>タイトル</dt>
                  <dd>{title}</dd>
                  <dt>内容</dt>
                  <dd className="notice-review-body">{content}</dd>
                  <dt>確認締切日</dt>
                  <dd>{readDeadlineDate ? formatDate(readDeadlineDate) : "未設定"}</dd>
                  <dt>掲示最終日</dt>
                  <dd>{displayDate ? formatDate(displayDate) : "未設定"}</dd>
                  <dt>返信受付</dt>
                  <dd style={{ fontWeight: "bold", color: allowReply ? "#0284c7" : "#64748b" }}>
                    {!allowReply && "なし"}
                    {allowReply && replyTargetMode === "all" && "あり"}
                    {allowReply && replyTargetMode === "specific" && (
                      <span>
                        あり（指定の {specificReplyIds.length} 名のみ:{" "}
                        {employees
                          .filter((e) => specificReplyIds.includes(e.memberId))
                          .map((e) => e.name)
                          .join("、") || "未選択"}
                        ）
                      </span>
                    )}
                  </dd>
                  <dt>添付ファイル</dt>
                  <dd>
                    {savedAttachments.length + pendingFiles.length === 0
                      ? "なし"
                      : `${savedAttachments.length}件保存済み / ${pendingFiles.length}件追加予定`}
                  </dd>
                  <dt>配信先</dt>
                  <dd>
                    {selectedIds.length} 名
                    {selectedPreviewNames.length > 0 && (
                      <ul className="notice-review-recipients">
                        {selectedPreviewNames.slice(0, 30).map((n) => (
                          <li key={n}>{n}</li>
                        ))}
                        {selectedPreviewNames.length > 30 && (
                          <li>…他 {selectedPreviewNames.length - 30} 名</li>
                        )}
                      </ul>
                    )}
                  </dd>
                </dl>
                <div className="btn-group-notice notice-review-actions">
                  <button
                    className="btn-save"
                    type="button"
                    onClick={handleFinalSave}
                    disabled={loading}
                  >
                    保存
                  </button>
                  <button
                    className="btn-secondary-notice"
                    type="button"
                    onClick={handleReviewBack}
                    disabled={loading}
                  >
                    修正
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="form-group">
                  <label>タイトル：</label>
                  <input
                    type="text"
                    className="input-title"
                    list="notice-title-history"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="タイトルを入力…"
                  />
                  <datalist id="notice-title-history">
                    {titleHistory.map((h) => (
                      <option key={h} value={h} />
                    ))}
                  </datalist>
                </div>

                <div className="form-group">
                  <label htmlFor="read-deadline-nc">確認締切日（任意）：</label>
                  <DatePartsInput
                    name="read-deadline-nc"
                    value={readDeadlineDate}
                    onChange={setReadDeadlineDate}
                    min={editingNoticeId ? undefined : todayYmd()}
                  />
                  <p className="field-hint">
                    設定すると「既読確認」で未読者を表示できます。締切の前日に未読者へリマインド通知を自動送信します（1回限り）。
                  </p>
                </div>
                <div className="form-group">
                  <label htmlFor="display-date">掲示最終日（任意）：</label>
                  <DatePartsInput
                    name="display-date"
                    value={displayDate}
                    onChange={setDisplayDate}
                    min={editingNoticeId ? undefined : todayYmd()}
                  />
                  <p className="field-hint">
                    設定すると、指定日までお知らせが掲示されます。
                  </p>
                </div>

                <div className="form-group">
                  <label>お知らせ内容：</label>
                  <textarea
                    className="input-content"
                    rows="8"
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="お知らせ内容を入力…"
                  />
                </div>

                {/* 返事 */}
                {/*}
                <div className="form-group" style={{ margin: "14px 0", padding: "8px 12px", background: "#f8fafc", borderRadius: "6px", border: "1px solid #e2e8f0" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontWeight: "bold", fontSize: "0.95rem", color: "#1e293b", margin: 0 }}>
                    <input
                      type="checkbox"
                      checked={allowReply}
                      onChange={(e) => setAllowReply(e.target.checked)}
                      style={{ width: "18px", height: "18px", cursor: "pointer" }}
                    />
                    <span>社員からの返信・回答を受け付ける</span>
                  </label>
                  <p style={{ margin: "4px 0 0 26px", fontSize: "0.82rem", color: "#64748b" }}>
                    チェックを入れると、配信先の社員がお知らせ画面から管理者へ回答・返信できるようになります。
                  </p>
                </div>
                */}
                {/* ===== 返事・回答設定 ===== */}
                <div
                  className="form-group"
                  style={{
                    margin: "16px 0",
                    padding: "12px 14px",
                    background: "#f8fafc",
                    borderRadius: "8px",
                    border: "1px solid #cbd5e1",
                  }}
                >
                  {/* Checkbox 返事 */}
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      cursor: "pointer",
                      fontWeight: "bold",
                      fontSize: "0.95rem",
                      color: "#0f172a",
                      margin: 0,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={allowReply}
                      onChange={(e) => {
                        setAllowReply(e.target.checked);
                        if (!e.target.checked) {
                          setSpecificReplyIds([]);
                          setReplyTargetMode("all");
                        }
                      }}
                      style={{ width: "18px", height: "18px", cursor: "pointer" }}
                    />
                    <span>社員からの返信・回答を受け付ける</span>
                  </label>
                  {/*}
                  <p style={{ margin: "4px 0 0 26px", fontSize: "0.82rem", color: "#64748b" }}>
                    チェックを入れると、配信先または指定された社員がお知らせ画面から管理者へ回答できるようになります。
                  </p>
                  */}
                  {/* allowReply */}
                  {allowReply && (
                    <div style={{ marginTop: 12, paddingLeft: 26, borderTop: "1px dashed #e2e8f0", paddingTop: 10 }}>
                      <div style={{ display: "flex", gap: 24, marginBottom: 10, flexWrap: "nowrap", whiteSpace: "nowrap" }}>
                        <label style={{ cursor: "pointer", fontSize: "0.9rem", display: "flex", alignItems: "center", gap: 6 }}>
                          <input
                            type="radio"
                            name="replyTargetMode"
                            value="all"
                            checked={replyTargetMode === "all"}
                            onChange={() => setReplyTargetMode("all")}
                          />
                          配信先の全員
                        </label>
                        <label style={{ cursor: "pointer", fontSize: "0.9rem", display: "flex", alignItems: "center", gap: 6 }}>
                          <input
                            type="radio"
                            name="replyTargetMode"
                            value="specific"
                            checked={replyTargetMode === "specific"}
                            onChange={() => setReplyTargetMode("specific")}
                          />
                          特定の社員のみ（指定）
                        </label>
                      </div>

                      {/* 社員返事 */}
                      {replyTargetMode === "specific" && (
                        <div style={{ background: "#ffffff", padding: "10px", borderRadius: "6px", border: "1px solid #e2e8f0" }}>
                          <p style={{ margin: "0 0 8px 0", fontSize: "0.82rem", fontWeight: "bold", color: "#334155" }}>
                            返信を求める社員を選択してください（{specificReplyIds.length}名指定中）:
                          </p>

                          {selectedIds.length === 0 ? (
                            <p style={{ margin: 0, fontSize: "0.82rem", color: "#ef4444" }}>
                              ※ 先に左側のリストから配信先の社員を選択してください。
                            </p>
                          ) : (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", maxHeight: "130px", overflowY: "auto" }}>
                              {employees
                                .filter((emp) => selectedIds.includes(emp.memberId))
                                .map((emp) => {
                                  const isChecked = specificReplyIds.includes(emp.memberId);
                                  return (
                                    <label
                                      key={emp.memberId}
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "6px",
                                        padding: "4px 10px",
                                        borderRadius: "4px",
                                        background: isChecked ? "#e0f2fe" : "#f1f5f9",
                                        border: isChecked ? "1px solid #0284c7" : "1px solid #cbd5e1",
                                        fontSize: "0.85rem",
                                        cursor: "pointer",
                                      }}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => {
                                          setSpecificReplyIds((prev) =>
                                            isChecked
                                              ? prev.filter((id) => id !== emp.memberId)
                                              : [...prev, emp.memberId]
                                          );
                                        }}
                                      />
                                      <span>{emp.name}（{emp.memberId}）</span>
                                    </label>
                                  );
                                })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                

                {/* ===== 添付ファイル ===== */}
                <div className="form-group">
                  <label>添付ファイル（PDF・画像等）：</label>
                  {/* ドラッグ&ドロップゾーン */}
                  <div
                    className={`notice-drop-zone${isDragOver ? " notice-drop-zone-over" : ""}`}
                    onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                    onDragLeave={() => setIsDragOver(false)}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {/*<div className="notice-drop-icon">📎</div>*/}
                    <div className="notice-drop-text">
                      ここにファイルをドロップ、またはクリックして選択
                    </div>
                    <div className="notice-drop-hint">PDF・Word・Excel・画像 / 1ファイル上限 20MB</div>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.zip"
                    style={{ display: "none" }}
                    onChange={handleFileSelect}
                  />
                  {/* 保存済み添付ファイル */}
                  {savedAttachments.length > 0 && (
                    <div className="notice-attachment-list">
                      <div className="notice-attachment-header" style={{ color: "#dc2626" }}>保存済みファイル</div>
                      {savedAttachments.map((att) => (
                        <div key={att.id} className="notice-attachment-item">
                          <span className="notice-att-icon">📄</span>
                          <a
                            href={att.api_url}
                            onClick={(e) => {
                              // 認証付きの配信 API から取得して開く（docs/spec_file_access.md）
                              e.preventDefault();
                              openAuthFile(att.api_url, att.filename).catch((err) =>
                                showToast(authFileErrorMessage(err), true)
                              );
                            }}
                            className="notice-att-name"
                          >
                            {att.filename}
                          </a>
                          <span className="notice-att-size">{formatFileSize(att.file_size)}</span>
                          <button
                            type="button"
                            className="notice-att-remove"
                            onClick={() => removeAttachment(att.id)}
                            title="削除"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* 追加予定ファイル */}
                  {pendingFiles.length > 0 && (
                    <div className="notice-attachment-list">
                      <div className="notice-attachment-header">追加予定ファイル（保存時にアップロード）</div>
                      {pendingFiles.map((pf, i) => (
                        <div key={i} className="notice-attachment-item notice-attachment-pending">
                          <span className="notice-att-icon">📎</span>
                          <span className="notice-att-name">{pf.name}</span>
                          <span className="notice-att-size">{formatFileSize(pf.size)}</span>
                          <button
                            type="button"
                            className="notice-att-remove"
                            onClick={() => removePendingFile(i)}
                            title="削除"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="btn-group-notice">
                  {editingNoticeId && !isEditingMode && (
                  <button
                    type="button"
                    className="btn-reduce"
                    onClick={handleStartEdit}
                  >
                    修正
                  </button>
                  )}
                  <button
                    className="btn-save"
                    type="button"
                    onClick={handleConfirmClick}
                    disabled={loading}
                  >
                    確認
                  </button>
                  <button
                    className="btn-secondary-notice"
                    type="button"
                    onClick={handleTempSave}
                    disabled={loading}
                  >
                    一時保存
                  </button>
                  <button
                    className="btn-cancel-notice"
                    type="button"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    キャンセル
                  </button>
                  {editingNoticeId && (
                    <button
                      className="btn-copy-notice"
                      type="button"
                      onClick={handleDuplicate}
                      disabled={loading}
                      title="現在の内容を残したまま新しいお知らせにします（少し修正して別の人に送る用）"
                    >
                      複製
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      ) : (
        /* ===== 一覧タブ ===== */
        <div className="notice-list-tab-wrap">
          <div className="notice-list-tab-header">
            <h3 className="notice-list-tab-title">お知らせ一覧</h3>
            <button
              className="btn-save"
              type="button"
              style={{ marginLeft: "auto" }}
              onClick={() => setActiveTab("create")}
            >
              ＋ 新規作成
            </button>
          </div>

          {/* 一時保存の下書き表示 */}
          {(() => {
            try {
              const raw = localStorage.getItem(FORM_DRAFT_KEY);
              if (!raw) return null;
              const d = JSON.parse(raw);
              if (!d || !(d.title || d.content)) return null;
              return (
                <div
                  className="notice-draft-item"
                  onClick={() => {
                    // ドラフトを読み込んで作成タブへ
                    if (d.title != null) setTitle(String(d.title));
                    if (d.content != null) setContent(String(d.content));
                    if (Array.isArray(d.selectedIds)) setSelectedIds(d.selectedIds);
                    if (d.readDeadlineDate != null) setReadDeadlineDate(String(d.readDeadlineDate));
                    if (d.displayDate != null) setDisplayDate(String(d.displayDate));
                    if (d.editingNoticeId != null) setEditingNoticeId(d.editingNoticeId);
                    if (typeof d.committeeOnly === "boolean") setCommitteeOnly(d.committeeOnly);
                    if (d.selectedDeptId != null) setSelectedDeptId(String(d.selectedDeptId));
                    if (d.selectedKaName != null) setSelectedKaName(String(d.selectedKaName));
                    setActiveTab("create");
                    showToast("一時保存した内容を読み込みました。");
                  }}
                >
                  <span className="notice-draft-badge">下書き</span>
                  <span className="notice-draft-title">{d.title || "（タイトルなし）"}</span>
                  <span className="notice-draft-note">一時保存（この端末のみ）— クリックして編集</span>
                  <button
                    type="button"
                    className="notice-att-remove"
                    style={{ marginLeft: "auto" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      localStorage.removeItem(FORM_DRAFT_KEY);
                      showToast("一時保存を削除しました。");
                      // force re-render
                      setNoticePage((p) => p);
                    }}
                    title="下書きを削除"
                  >
                    ✕
                  </button>
                </div>
              );
            } catch (_) { return null; }
          })()}

          {(() => {
            const CIRCLED = ['①','②','③','④','⑤','⑥','⑦','⑧','⑨','⑩','⑪','⑫','⑬','⑭','⑮','⑯','⑰','⑱','⑲','⑳'];
            const circledNum = (n) => n <= 20 ? CIRCLED[n - 1] : `(${n})`;
            const totalPages = Math.ceil(recentNotices.length / NOTICES_PER_PAGE);
            const pagedNotices = recentNotices.slice(
              (noticePage - 1) * NOTICES_PER_PAGE,
              noticePage * NOTICES_PER_PAGE
            );
            return (
              <>
                <ul className="notice-list-simple">
                  {pagedNotices.length > 0 ? (
                    pagedNotices.map((notice, index) => {
                      const num = (noticePage - 1) * NOTICES_PER_PAGE + index + 1;
                      return (
                      <li
                        key={notice.id}
                        className={`notice-item-simple ${
                          editingNoticeId === notice.id && isEditingMode ? "is-editing" : ""
                        }`}
                        onClick={() => handleEditClick(notice)}
                        style={{ cursor: "pointer" }}
                      >
                        <span className="notice-serial-num">{circledNum(num)}</span>
                        <span className="notice-date">{formatDate(notice.created_at)}</span>
                        <span className="notice-separator"> </span>
                        <span className="notice-title-text" title={notice.title}>
                          {notice.title}
                        </span>
                        {notice.attachment_count > 0 && (
                          <span className="notice-attachment-badge" title="添付ファイルあり">
                            📎{notice.attachment_count}
                          </span>
                        )}
                        {notice.read_deadline_date && (
                          <span className="notice-deadline-badge" title="確認締切日">
                            確認日 {formatDate(notice.read_deadline_date)}
                          </span>
                        )}
                        {notice.publish_end_at && (
                          <span className="notice-display-deadline-badge" title="掲示最終日">
                            掲示期限 {formatDate(notice.publish_end_at)}
                          </span>
                        )}
                        {editingNoticeId === notice.id && isEditingMode && (
                          <span className="edit-indicator">編集中</span>
                        )}
                        <button
                          className="btn-readers"
                          onClick={(e) => openReadersPage(notice, e)}
                          title="既読・未読の確認"
                          type="button"
                        >
                          既読確認
                        </button>
                        <button
                          className="btn-delete-notice"
                          onClick={(e) => handleDeleteNotice(notice, e)}
                          title="このお知らせを削除"
                          type="button"
                        >
                          削除
                        </button>
                      </li>
                      );
                    })
                  ) : (
                    <li className="no-notices">現在、お知らせはありません。</li>
                  )}
                </ul>
                {totalPages > 1 && (
                  <div className="notice-pagination">
                    <button
                      className="notice-page-btn"
                      disabled={noticePage === 1}
                      onClick={() => setNoticePage((p) => p - 1)}
                      type="button"
                    >
                      ‹
                    </button>
                    <span className="notice-page-info">
                      {noticePage} / {totalPages}
                    </span>
                    <button
                      className="notice-page-btn"
                      disabled={noticePage === totalPages}
                      onClick={() => setNoticePage((p) => p + 1)}
                      type="button"
                    >
                      ›
                    </button>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {toast.visible && (
        <div
          className={`notice-toast ${toast.isError ? "notice-toast-error" : "notice-toast-success"}`}
        >
          {toast.text}
        </div>
      )}
    </div>
  );
};

export default NoticeCreate;
